import Section from "@/components/Section";
import { Button } from "@/components/ui/button";
import { Check, Star } from "lucide-react";

const plans = [
  {
    title: "2 séances / semaine",
    price: "64 €",
    unit: "/mois",
    highlight: false,
    features: ["Accès à 2 créneaux de cours par semaine", "Session de 1h parmi les créneaux WOD et Free"],
  },
  {
    title: "3 séances / semaine",
    price: "74 €",
    unit: "/mois",
    highlight: true,
    features: ["Accès à 3 créneaux de cours par semaine", "Session de 1h parmi les créneaux WOD et Free"],
  },
  {
    title: "Illimité",
    price: "90 €",
    unit: "/mois",
    highlight: false,
    features: ["Accès illimité aux cours", "Créneaux WOD et Free inclus"],
  },
  {
    title: "Option Suivi diététique",
    price: "50 €",
    unit: "/mois",
    highlight: false,
    features: ["Suivi individuel par une diététicienne nutritionniste diplômée d'État, en plus de votre abonnement à la box"],
  },
];

const extras = [
  { title: "Kids", price: "40 € TTC/mois", detail: "Réduction -5 € si parent abonné" },
  { title: "Renfo", price: "25 € TTC/mois", detail: "Accès libre à la box de l'ouverture jusqu'à 12h" },
  { title: "Carnet 10 séances", price: "140 € TTC", detail: "Validité 1 an" },
  { title: "Carnet 20 séances", price: "220 € TTC", detail: "Validité 1 an" },
  { title: "Drop-in", price: "15 € TTC", detail: "Séance à l'unité hors abonnement pour les pratiquants de passage" },
  { title: "Coaching individuel", price: "Sur devis", detail: "Programmation personnalisée" },
];

export default function PricingSection() {
  const scrollTo = () => document.querySelector("#contact")?.scrollIntoView({ behavior: "smooth" });

  return (
    <Section id="tarifs">
      <div className="text-center mb-12">
        <h2 className="text-4xl md:text-5xl text-foreground">Tarifs & abonnements</h2>
      </div>

      {/* Main plans */}
      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-10">
        {plans.map((p) => (
          <div
            key={p.title}
            className={`bg-card border rounded-lg p-6 flex flex-col hover:shadow-lg transition-all duration-300 ${
              p.highlight ? "border-cta ring-2 ring-cta/20" : "border-border"
            }`}
          >
            {p.highlight && (
              <span className="inline-block self-start text-xs font-bold text-cta-foreground bg-cta px-3 py-1 rounded-full mb-3 font-body normal-case">Le + populaire</span>
            )}
            <h3 className="text-xl text-foreground">{p.title}</h3>
            <div className="mt-2 mb-4">
              <span className="text-3xl font-heading text-foreground">{p.price}</span>
              <span className="text-sm text-muted-foreground font-body normal-case"> TTC</span>
              {p.unit && <span className="text-sm text-muted-foreground font-body normal-case">{p.unit}</span>}
            </div>
            <ul className="space-y-2 flex-1 mb-6">
              {p.features.map((f) => (
                <li key={f} className="flex items-start gap-2 text-sm text-muted-foreground font-body normal-case">
                  <Check size={16} className="text-success shrink-0 mt-0.5" />
                  {f}
                </li>
              ))}
            </ul>
            <Button
              onClick={scrollTo}
              className={`w-full rounded-lg font-semibold ${
                p.highlight
                  ? "bg-cta text-cta-foreground hover:bg-cta/90"
                  : "bg-charcoal text-charcoal-foreground hover:bg-charcoal/90"
              }`}
            >
              {p.highlight ? "Réserver maintenant" : "Nous contacter"}
            </Button>
          </div>
        ))}
      </div>

      {/* Extras */}
      <div className="bg-card border border-border rounded-lg p-8 mb-8">
        <h3 className="text-2xl text-foreground mb-6 text-center">Formules complémentaires</h3>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {extras.map((e) => (
            <div key={e.title} className="flex items-start gap-3 p-4 bg-background border border-border rounded-lg">
              <Star size={18} className="text-gold shrink-0 mt-0.5" />
              <div>
                <div className="flex items-baseline gap-2">
                  <span className="text-sm font-semibold text-foreground">{e.title}</span>
                  <span className="text-sm font-heading text-gold">{e.price}</span>
                </div>
                <p className="text-xs text-muted-foreground font-body normal-case mt-0.5">{e.detail}</p>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Reductions */}
      <div className="bg-gold/10 border border-gold/20 rounded-lg p-6 text-center mb-6">
        <p className="text-sm font-semibold text-gold font-body normal-case">
          🎉 Réduction de -5 €/mois pour les couples, étudiants et forces de l'ordre
        </p>
      </div>

      <p className="text-center text-xs text-muted-foreground font-body normal-case max-w-2xl mx-auto leading-relaxed">
        Frais d'inscription : 50 € · Voir <a href="/legal/cgv" className="text-gold hover:underline">CGV</a>. Abonnement mensuel résiliable avec 2 mois de préavis sauf conditions particulières (déménagement, incapacité…). Contactez les coachs pour plus de détails.
      </p>
    </Section>
  );
}
